import type { ProfessionalSummaryEntry } from '@applicator/shared'
import type { SupabaseClient } from '@supabase/supabase-js'

export type AccountSummaryVariantSourceKind = 'resume' | 'snapshot'

export interface AccountSummaryVariant {
  key: string
  text: string
  sourceResumeId: string
  sourceResumeTitle: string
  sourceLabel: string
  sourceKind: AccountSummaryVariantSourceKind
}

export function normalizeSummaryTextForDedupe(text: string): string {
  return (text ?? '').trim().replace(/\s+/g, ' ')
}

function variantKey(
  sourceKind: AccountSummaryVariantSourceKind,
  sourceResumeId: string,
  normalizedText: string,
): string {
  return `${sourceKind}:${sourceResumeId}:${normalizedText}`
}

function extractSummaryTextsFromContent(content: unknown): string[] {
  if (!Array.isArray(content)) return []
  const texts: string[] = []
  for (const item of content) {
    if (!item || typeof item !== 'object') continue
    const entry = item as ProfessionalSummaryEntry
    const text = (entry.text ?? '').trim()
    if (text) texts.push(text)
  }
  return texts
}

function sortVariants(
  variants: AccountSummaryVariant[],
  currentResumeId?: string,
): AccountSummaryVariant[] {
  return [...variants].sort((a, b) => {
    const aCurrent = currentResumeId && a.sourceResumeId === currentResumeId
    const bCurrent = currentResumeId && b.sourceResumeId === currentResumeId
    if (aCurrent && !bCurrent) return -1
    if (!aCurrent && bCurrent) return 1
    return a.sourceLabel.localeCompare(b.sourceLabel, undefined, { sensitivity: 'base' })
  })
}

export async function fetchAccountProfessionalSummaryVariants(
  supabase: SupabaseClient,
  options: { currentResumeId?: string } = {},
): Promise<AccountSummaryVariant[]> {
  const { currentResumeId } = options

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!user) return []

  const { data: resumes, error: resumesError } = await supabase
    .from('resumes')
    .select('id, title')
    .eq('user_id', user.id)

  if (resumesError) throw resumesError

  const resumeRows = resumes ?? []
  if (resumeRows.length === 0) return []

  const titleById = new Map(resumeRows.map((r) => [r.id, r.title ?? 'Resume']))
  const resumeIds = resumeRows.map((r) => r.id)

  const { data: contentRows, error: contentError } = await supabase
    .from('resume_content')
    .select('resume_id, content')
    .in('resume_id', resumeIds)
    .eq('section_type', 'professional_summary')

  if (contentError) throw contentError

  const { data: snapshotRows, error: snapshotError } = await supabase
    .from('resume_summary_quality_snapshots')
    .select('resume_id, summary_text, created_at')
    .eq('user_id', user.id)
    .eq('stage', 'final')
    .order('created_at', { ascending: false })

  if (snapshotError) throw snapshotError

  const seen = new Set<string>()
  const variants: AccountSummaryVariant[] = []

  const resumeIdOrder = currentResumeId
    ? [currentResumeId, ...resumeIds.filter((id) => id !== currentResumeId)]
    : resumeIds

  const contentByResume = new Map<string, unknown>()
  for (const row of contentRows ?? []) {
    contentByResume.set(row.resume_id, row.content)
  }

  for (const resumeId of resumeIdOrder) {
    const content = contentByResume.get(resumeId)
    if (content === undefined) continue
    const title = titleById.get(resumeId) ?? 'Resume'
    for (const text of extractSummaryTextsFromContent(content)) {
      const normalized = normalizeSummaryTextForDedupe(text)
      if (!normalized || seen.has(normalized)) continue
      seen.add(normalized)
      variants.push({
        key: variantKey('resume', resumeId, normalized),
        text,
        sourceResumeId: resumeId,
        sourceResumeTitle: title,
        sourceLabel: title,
        sourceKind: 'resume',
      })
    }
  }

  for (const snap of snapshotRows ?? []) {
    const text = (snap.summary_text ?? '').trim()
    const normalized = normalizeSummaryTextForDedupe(text)
    if (!normalized || seen.has(normalized)) continue
    seen.add(normalized)
    const title = titleById.get(snap.resume_id) ?? 'Resume'
    variants.push({
      key: variantKey('snapshot', snap.resume_id, normalized),
      text,
      sourceResumeId: snap.resume_id,
      sourceResumeTitle: title,
      sourceLabel: `${title} (tailoring)`,
      sourceKind: 'snapshot',
    })
  }

  return sortVariants(variants, currentResumeId)
}

export function adoptAccountSummaryVariant(
  currentEntries: ProfessionalSummaryEntry[],
  variant: AccountSummaryVariant,
): ProfessionalSummaryEntry[] {
  const newEntry: ProfessionalSummaryEntry = {
    id: `summary-${Date.now()}`,
    text: variant.text,
    included: true,
  }
  const deselected = currentEntries.map((entry) => ({ ...entry, included: false }))
  return [newEntry, ...deselected]
}
